import React, { useState, useEffect, Fragment, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { format, addDays } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronLeftIcon, 
  ChevronRightIcon, 
  ViewColumnsIcon,
  ChevronDownIcon
} from '@heroicons/react/24/solid';
import { setSelectedDate } from '../state';
import { Menu, Transition } from '@headlessui/react';
import NewEventForm from './NewEventForm';
import CalendarShareRequestsDialog from './CalendarShareRequestsDialog';
import { FaCalendarAlt, FaBell } from 'react-icons/fa';
import calendarService from '../../../services/calendarService';

export default function Header({ 
  currentView, 
  setCurrentView, 
  onEventAdded, 
  onOpenShareRequests,
  children  // Add children prop to receive the MealCalendarToggle
}) {
  const dispatch = useDispatch();
  const selectedDate = useSelector((state) => new Date(state.calendar.selectedDate));
  const [isNewEventFormOpen, setIsNewEventFormOpen] = React.useState(false);
  const [isShareRequestsOpen, setIsShareRequestsOpen] = useState(false);
  const [isViewMenuOpen, setIsViewMenuOpen] = useState(false);
  const viewMenuRef = useRef(null);
  const user = useSelector(state => state.auth.user);
  
  // State for holding share requests directly
  const [shareRequests, setShareRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Fetch share requests directly using the service
  const fetchShareRequests = async () => {
    if (!user?.id) return;
    
    setLoading(true);
    try {
      const response = await calendarService.fetchShareRequests(user.id);
      const requests = response.requests || [];
      console.log('Directly fetched share requests:', requests);
      
      setShareRequests(requests);
    } catch (error) {
      console.error('Error fetching share requests:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Calculate pending requests count from our local state
  const pendingRequestsCount = shareRequests.filter(req => req.status === 'pending').length;
  
  // Debug logging
  console.log('Share requests in header:', shareRequests);
  console.log('Pending requests count:', pendingRequestsCount);
  
  // Load share requests when component mounts
  useEffect(() => {
    fetchShareRequests();
  }, [user?.id]);
  
  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (viewMenuRef.current && !viewMenuRef.current.contains(event.target)) {
        setIsViewMenuOpen(false);
      }
    };
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [viewMenuRef]);

  const handlePreviousDay = () => {
    dispatch(setSelectedDate({ selectedDate: addDays(selectedDate, -1).toString() }));
  };

  const handleNextDay = () => {
    dispatch(setSelectedDate({ selectedDate: addDays(selectedDate, 1).toString() }));
  };

  const handleToday = () => {
    dispatch(setSelectedDate({ selectedDate: new Date().toString() }));
  };

  // Handlers for share requests
  const handleAcceptShare = (shareData) => {
    // Refresh share requests after accepting
    fetchShareRequests();
  };

  const handleRejectShare = (shareData) => {
    // Refresh share requests after rejecting
    fetchShareRequests();
  };
  
  // Handle invitations button click - use the prop if provided
  const handleInvitationsClick = () => {
    if (typeof onOpenShareRequests === 'function') {
      onOpenShareRequests();
    } else {
      setIsShareRequestsOpen(true);
    }
  };
  
  // Refresh share requests when dialog closes
  const handleCloseShareRequests = () => {
    setIsShareRequestsOpen(false);
    fetchShareRequests();
  };

  return (
    <div className="relative bg-gradient-to-b from-pri-dark/90 via-sec-dark/90 to-pri-dark/90 shadow-2xl">
      {/* Glossy Overlay with soft-pink accents */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-soft-pink/10 via-transparent to-mint-green/5" />
        <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent" />
        <div className="absolute -left-24 -top-24 w-48 h-48 bg-soft-pink/20 rounded-full filter blur-[80px] opacity-30" />
        <div className="absolute -right-24 -bottom-24 w-48 h-48 bg-mint-green/20 rounded-full filter blur-[80px] opacity-20" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-soft-pink/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      </div>

      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-2 sm:px-4 py-2 sm:py-4 lg:px-8">
        <div className="flex items-center justify-between gap-1 sm:gap-2">
          {/* Left Section - Navigation */}
          <div className="flex items-center gap-0.5 sm:gap-4 min-w-0">
            <motion.div 
              className="flex items-center space-x-0.5 sm:space-x-2 bg-gradient-to-br from-pri-dark/60 to-sec-dark/60 rounded-lg sm:rounded-xl p-0.5 sm:p-1 backdrop-blur-md border border-soft-pink/20 shadow-lg"
              whileHover={{ scale: 1.02, boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15)' }}
            >
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={handlePreviousDay}
                className="p-1 sm:p-1.5 rounded-md sm:rounded-lg text-pri-white bg-gradient-to-br from-soft-pink/10 to-mint-green/5 hover:from-soft-pink/20 hover:to-mint-green/10 transition-all duration-300"
              >
                <ChevronLeftIcon className="h-3.5 w-3.5 sm:h-5 sm:w-5" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleNextDay}
                className="p-1 sm:p-1.5 rounded-md sm:rounded-lg text-pri-white bg-gradient-to-br from-soft-pink/10 to-mint-green/5 hover:from-soft-pink/20 hover:to-mint-green/10 transition-all duration-300"
              >
                <ChevronRightIcon className="h-3.5 w-3.5 sm:h-5 sm:w-5" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleToday}
                className="px-1.5 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm font-medium text-pri-white bg-gradient-to-br from-soft-pink/10 to-mint-green/5 hover:from-soft-pink/20 hover:to-mint-green/10 rounded-md sm:rounded-lg transition-all duration-300"
              >
                Today
              </motion.button>
            </motion.div>

            <motion.h2 
              className="text-sm sm:text-lg font-semibold bg-gradient-to-r from-soft-pink to-mint-green bg-clip-text text-transparent whitespace-nowrap overflow-visible flex-shrink-0 mx-0.5 sm:mx-1"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              key={selectedDate.toString()}
            >
              <span className="hidden sm:inline">{format(selectedDate, 'MMMM d, yyyy')}</span>
              <span className="inline sm:hidden">{format(selectedDate, 'MMM d')}</span>
            </motion.h2>
          </div>

          {/* Right Section - View Selector and New Event */}
          <div className="flex items-center gap-1 sm:gap-3 flex-shrink-0">
            {/* Add children here, to render MealCalendarToggle if provided */}
            <div className="mr-0.5 sm:mr-2">
              {children}
            </div>
            
            {/* Calendar Share Requests Button */}
            <div className="relative">
              <motion.button
                whileHover={{ scale: 1.05, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)' }}
                whileTap={{ scale: 0.95 }}
                onClick={handleInvitationsClick}
                className="flex items-center justify-center p-1 sm:p-2 rounded-lg sm:rounded-xl bg-gradient-to-br from-soft-pink/15 to-mint-green/10 text-pri-white border border-soft-pink/20 hover:from-soft-pink/25 hover:to-mint-green/20 transition-all duration-300 shadow-lg"
              >
                <FaCalendarAlt className="h-3.5 w-3.5 sm:h-5 sm:w-5 text-soft-pink" />
                <span className="ml-2 text-sm font-medium hidden sm:inline">Invitations</span>
              </motion.button>
              
              {pendingRequestsCount > 0 && (
                <span className="absolute -top-1 -right-1 sm:-top-2 sm:-right-2 flex items-center justify-center h-4 w-4 sm:h-5 sm:w-5 text-[10px] sm:text-xs font-bold rounded-full bg-soft-pink text-pri-dark shadow-lg animate-pulse">
                  {pendingRequestsCount}
                </span>
              )}
            </div>
            
            {/* View selector dropdown - custom implementation */}
            <div className="relative" ref={viewMenuRef}>
              <motion.div 
                whileHover={{ scale: 1.05, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)' }} 
                whileTap={{ scale: 0.95 }}
                className="backdrop-blur-sm"
              >
                <button 
                  className="flex items-center gap-0.5 sm:gap-2 px-1.5 sm:px-3 py-1 sm:py-2 rounded-lg sm:rounded-xl bg-gradient-to-br from-pri-dark/60 to-sec-dark/60 text-pri-white border border-soft-pink/20 hover:from-soft-pink/15 hover:to-mint-green/10 transition-all duration-300 shadow-lg"
                  onClick={() => setIsViewMenuOpen(!isViewMenuOpen)}
                >
                  <ViewColumnsIcon className="h-3.5 w-3.5 sm:h-5 sm:w-5" />
                  <span className="text-xs sm:text-sm font-medium hidden sm:inline">{currentView.charAt(0).toUpperCase() + currentView.slice(1)}</span>
                  <ChevronDownIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                </button>
              </motion.div>
              
              <AnimatePresence>
                {isViewMenuOpen && (
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.95, y: -10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute right-0 mt-2 w-32 sm:w-48 origin-top-right bg-gradient-to-br from-pri-dark/95 to-sec-dark/95 backdrop-blur-xl border border-soft-pink/20 rounded-xl shadow-2xl z-50 overflow-hidden"
                  >
                    <div className="py-1 space-y-1">
                      {Object.keys(viewComponents).map((view) => (
                        <button
                          key={view}
                          onClick={() => {
                            setCurrentView(view);
                            setIsViewMenuOpen(false);
                          }}
                          className={`${
                            currentView === view ? 'text-soft-pink' : 'text-pri-white'
                          } group flex items-center w-full px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm transition-colors duration-200 hover:bg-gradient-to-r hover:from-soft-pink/15 hover:to-mint-green/10`}
                        >
                          {view.charAt(0).toUpperCase() + view.slice(1)}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <motion.button
              whileHover={{ scale: 1.05, boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)' }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsNewEventFormOpen(true)}
              className="flex items-center justify-center px-2 sm:px-3 py-1 sm:py-2 rounded-lg sm:rounded-xl bg-gradient-to-br from-soft-pink to-mint-green text-pri-dark font-medium shadow-lg"
            >
              <span className="hidden sm:inline text-sm font-medium">+ Event</span>
              <span className="inline sm:hidden text-xs font-medium">+</span>
            </motion.button>
          </div>
        </div>
      </div>

      {/* New Event Form */}
      <NewEventForm
        isOpen={isNewEventFormOpen}
        onClose={() => setIsNewEventFormOpen(false)}
        onEventAdded={onEventAdded}
      />

      {/* Calendar Share Requests Dialog - only render if onOpenShareRequests is not provided */}
      {!onOpenShareRequests && (
        <CalendarShareRequestsDialog
          isOpen={isShareRequestsOpen}
          onClose={handleCloseShareRequests}
          onAccept={handleAcceptShare}
          onReject={handleRejectShare}
          shareRequests={shareRequests}
          loading={loading}
          fetchShareRequests={fetchShareRequests}
        />
      )}
    </div>
  );
}
// Array of calendar view options
const viewComponents = {
  day: 'Day',
  week: 'Week',
  month: 'Month',
};

